@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_shared

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.MouseEvent

private const val NOTE_DRAW_WIDTH = 900
private const val NOTE_DRAW_HEIGHT = 520

internal fun croppedNoteDrawingDataUrl(canvas: HTMLCanvasElement): String {
    val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return canvas.toDataURL("image/png")
    val width = canvas.width
    val height = canvas.height
    if (width <= 0 || height <= 0) return canvas.toDataURL("image/png")
    val pixels = ctx.getImageData(0.0, 0.0, width.toDouble(), height.toDouble()).data
    var minX = width
    var minY = height
    var maxX = -1
    var maxY = -1
    var index = 3
    var pixel = 0
    while (index < pixels.length) {
        if ((pixels.asDynamic()[index] as Number).toInt() != 0) {
            val x = pixel % width
            val y = pixel / width
            if (x < minX) minX = x
            if (x > maxX) maxX = x
            if (y < minY) minY = y
            if (y > maxY) maxY = y
        }
        pixel++
        index += 4
    }
    if (maxX < minX || maxY < minY) return canvas.toDataURL("image/png")
    val scale = (canvas.width.toDouble() / canvas.getBoundingClientRect().width.coerceAtLeast(1.0)).coerceAtLeast(1.0)
    val padding = (8.0 * scale).toInt().coerceAtLeast(4)
    val cropX = (minX - padding).coerceAtLeast(0)
    val cropY = (minY - padding).coerceAtLeast(0)
    val cropWidth = (maxX + padding - cropX + 1).coerceAtMost(width - cropX)
    val cropHeight = (maxY + padding - cropY + 1).coerceAtMost(height - cropY)
    val cropped = document.createElement("canvas") as HTMLCanvasElement
    cropped.width = cropWidth
    cropped.height = cropHeight
    val croppedContext = cropped.getContext("2d") as? CanvasRenderingContext2D ?: return canvas.toDataURL("image/png")
    croppedContext.drawImage(
        canvas,
        cropX.toDouble(), cropY.toDouble(), cropWidth.toDouble(), cropHeight.toDouble(),
        0.0, 0.0, cropWidth.toDouble(), cropHeight.toDouble()
    )
    return cropped.toDataURL("image/png")
}

internal fun noteDrawingViewportDataUrl(canvasId: String): String? {
    val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return null
    val viewport = document.getElementById("$canvasId-viewport") as? HTMLElement ?: return null
    val panX = canvas.getAttribute("data-pan-x")?.toDoubleOrNull() ?: 0.0
    val panY = canvas.getAttribute("data-pan-y")?.toDoubleOrNull() ?: 0.0
    val scaleX = canvas.width.toDouble() / canvas.offsetWidth.coerceAtLeast(1)
    val scaleY = canvas.height.toDouble() / canvas.offsetHeight.coerceAtLeast(1)
    val sourceX = (-panX * scaleX).coerceIn(0.0, canvas.width.toDouble())
    val sourceY = (-panY * scaleY).coerceIn(0.0, canvas.height.toDouble())
    val sourceWidth = (viewport.clientWidth * scaleX).coerceAtMost(canvas.width - sourceX).coerceAtLeast(1.0)
    val sourceHeight = (viewport.clientHeight * scaleY).coerceAtMost(canvas.height - sourceY).coerceAtLeast(1.0)
    val preview = document.createElement("canvas") as HTMLCanvasElement
    preview.width = sourceWidth.toInt().coerceAtLeast(1)
    preview.height = sourceHeight.toInt().coerceAtLeast(1)
    val context = preview.getContext("2d") as? CanvasRenderingContext2D ?: return null
    context.drawImage(canvas, sourceX, sourceY, sourceWidth, sourceHeight, 0.0, 0.0, sourceWidth, sourceHeight)
    return preview.toDataURL("image/png")
}

@Composable
internal fun NoteDrawingOverlay(
    canvasId: String,
    initialImageDataUrl: String? = null,
    initialColor: String = "#ffffff",
    showControls: Boolean = true,
    interactionMode: String = "DRAW",
    expandableCanvas: Boolean = false,
    topInsetPx: Int = 0,
    bottomInsetPx: Int = 0,
    initialPanX: Double = 0.0,
    initialPanY: Double = 0.0,
    onColorChange: (String) -> Unit = {},
    onCancel: () -> Unit,
    onInsert: (String) -> Unit
) {
    var selectedColor by remember { mutableStateOf(initialColor) }
    val landscapeWidthPx = remember(expandableCanvas) {
        if (expandableCanvas) (window.innerWidth * 3).coerceAtLeast(1200) else 0
    }
    val landscapeHeightPx = remember(expandableCanvas) {
        if (expandableCanvas) (window.innerHeight * 3).coerceAtLeast(1800) else 0
    }
    Div({
        style {
            position(Position.Absolute)
            top(topInsetPx.px)
            right(0.px)
            bottom(bottomInsetPx.px)
            left(0.px)
            property("z-index", "20")
            backgroundColor(Color.transparent)
            overflow("hidden")
        }
    }) {
        Div({
            id("$canvasId-viewport")
            style {
                width(100.percent)
                height(100.percent)
                padding(10.px)
                property("box-sizing", "border-box")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
                color(Color.white)
                if (expandableCanvas) overflow("hidden")
            }
        }) {
            Canvas(attrs = {
                id(canvasId)
                style {
                    width(if (expandableCanvas) landscapeWidthPx.px else 100.percent)
                    if (expandableCanvas) {
                        height(landscapeHeightPx.px)
                        property("flex", "0 0 auto")
                    } else {
                        property("flex", "1")
                        minHeight(0.px)
                    }
                    backgroundColor(Color.transparent)
                    border(0.px)
                    property("touch-action", "none")
                    cursor("crosshair")
                    property("transform-origin", "top left")
                }
            })
            if (showControls) Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.FlexEnd)
                    gap(8.px)
                    property("flex-wrap", "wrap")
                }
            }) {
                NoteColorPicker(
                    color = selectedColor,
                    onColorChange = { drawColor ->
                        selectedColor = drawColor
                        onColorChange(drawColor)
                        val canvas = document.getElementById(canvasId) as? HTMLCanvasElement
                        val ctx = canvas?.getContext("2d") as? CanvasRenderingContext2D
                        if (ctx != null) ctx.strokeStyle = drawColor
                    }
                )
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                        property("flex-wrap", "wrap")
                        padding(5.px)
                        borderRadius(18.px)
                        property("background", "rgba(17,20,27,0.86)")
                    }
                }) {
                    Div({
                        attr("title", "Textmodus")
                        style {
                            width(34.px)
                            height(34.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            cursor("pointer")
                        }
                        onClick {
                            val canvas = document.getElementById(canvasId) as? HTMLCanvasElement
                                ?: return@onClick
                            onInsert(croppedNoteDrawingDataUrl(canvas))
                        }
                    }) {
                        Img(src = "images/format_color_text.svg", attrs = {
                            style { width(22.px); height(22.px) }
                        })
                    }
                    LumaticaButton(
                        label = "Leeren",
                        onClick = {
                            val canvas = document.getElementById(canvasId) as? HTMLCanvasElement
                            val ctx = canvas?.getContext("2d") as? CanvasRenderingContext2D
                            if (canvas != null && ctx != null) {
                                ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                            }
                        }
                    )
                    LumaticaButton(
                        label = "Einfügen",
                        variant = LumaticaButtonVariant.Action,
                        onClick = {
                            val canvas = document.getElementById(canvasId) as? HTMLCanvasElement
                                ?: return@LumaticaButton
                            onInsert(croppedNoteDrawingDataUrl(canvas))
                        }
                    )
                }
            }
        }
    }

    LaunchedEffect(Unit) {
        val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return@LaunchedEffect
        val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return@LaunchedEffect
        val canvasRect = canvas.getBoundingClientRect()
        val pixelRatio = window.devicePixelRatio.coerceAtLeast(1.0)
        canvas.width = (canvasRect.width * pixelRatio).toInt().coerceAtLeast(NOTE_DRAW_WIDTH)
        canvas.height = (canvasRect.height * pixelRatio).toInt().coerceAtLeast(NOTE_DRAW_HEIGHT)
        ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
        val coordinateScale = if (canvasRect.width > 0.0) {
            canvas.width.toDouble() / canvasRect.width
        } else {
            pixelRatio
        }
        ctx.lineWidth = 1.0 * coordinateScale
        ctx.strokeStyle = selectedColor
        ctx.asDynamic().lineCap = "round"
        ctx.asDynamic().lineJoin = "round"
        if (!initialImageDataUrl.isNullOrBlank()) {
            val sourceImage = document.createElement("img") as HTMLImageElement
            sourceImage.onload = {
                val imageWidth = sourceImage.naturalWidth.toDouble().coerceAtLeast(1.0)
                val imageHeight = sourceImage.naturalHeight.toDouble().coerceAtLeast(1.0)
                if (expandableCanvas) {
                    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                    ctx.drawImage(sourceImage, 0.0, 0.0, imageWidth, imageHeight)
                    val restoredScale = (canvas.width.toDouble() / canvas.getBoundingClientRect().width.coerceAtLeast(1.0)).coerceAtLeast(1.0)
                    ctx.lineWidth = 1.0 * restoredScale
                    ctx.strokeStyle = selectedColor
                    ctx.asDynamic().lineCap = "round"
                    ctx.asDynamic().lineJoin = "round"
                } else {
                    val scale = minOf(canvas.width / imageWidth, canvas.height / imageHeight)
                    val drawWidth = imageWidth * scale
                    val drawHeight = imageHeight * scale
                    val drawX = (canvas.width - drawWidth) / 2.0
                    val drawY = (canvas.height - drawHeight) / 2.0
                    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                    ctx.drawImage(sourceImage, drawX, drawY, drawWidth, drawHeight)
                }
                null
            }
            sourceImage.src = initialImageDataUrl
        }
        var drawing = false
        var panning = false
        var panStartX = 0.0
        var panStartY = 0.0
        var panOffsetX = initialPanX
        var panOffsetY = initialPanY
        var panStartOffsetX = 0.0
        var panStartOffsetY = 0.0

        fun moveLandscape(clientX: Double, clientY: Double) {
            val viewport = document.getElementById("$canvasId-viewport") as? HTMLElement ?: return
            val minX = (viewport.clientWidth - canvas.offsetWidth).toDouble().coerceAtMost(0.0)
            val minY = (viewport.clientHeight - canvas.offsetHeight).toDouble().coerceAtMost(0.0)
            panOffsetX = (panStartOffsetX + clientX - panStartX).coerceIn(minX, 0.0)
            panOffsetY = (panStartOffsetY + clientY - panStartY).coerceIn(minY, 0.0)
            canvas.style.transform = "translate(${panOffsetX}px, ${panOffsetY}px)"
            canvas.setAttribute("data-pan-x", panOffsetX.toString())
            canvas.setAttribute("data-pan-y", panOffsetY.toString())
        }

        canvas.style.transform = "translate(${panOffsetX}px, ${panOffsetY}px)"
        canvas.setAttribute("data-pan-x", panOffsetX.toString())
        canvas.setAttribute("data-pan-y", panOffsetY.toString())

        fun point(clientX: Double, clientY: Double): Pair<Double, Double> {
            val rect = canvas.getBoundingClientRect()
            return ((clientX - rect.left) / rect.width * canvas.width) to
                ((clientY - rect.top) / rect.height * canvas.height)
        }
        fun start(x: Double, y: Double) {
            if (canvas.getAttribute("data-interaction-mode") != "DRAW") return
            drawing = true
            ctx.beginPath()
            ctx.moveTo(x, y)
            ctx.lineTo(x, y)
            ctx.stroke()
        }
        fun draw(x: Double, y: Double) {
            if (canvas.getAttribute("data-interaction-mode") != "DRAW") return
            if (!drawing) return
            ctx.lineTo(x, y)
            ctx.stroke()
        }
        canvas.addEventListener("mousedown", {
            val event = it.unsafeCast<MouseEvent>()
            if (canvas.getAttribute("data-interaction-mode") == "PAN") {
                panning = true
                panStartX = event.clientX.toDouble()
                panStartY = event.clientY.toDouble()
                panStartOffsetX = panOffsetX
                panStartOffsetY = panOffsetY
                return@addEventListener
            }
            val (x, y) = point(event.clientX.toDouble(), event.clientY.toDouble())
            start(x, y)
        })
        canvas.addEventListener("mousemove", {
            val event = it.unsafeCast<MouseEvent>()
            if (panning) {
                moveLandscape(event.clientX.toDouble(), event.clientY.toDouble())
                return@addEventListener
            }
            val (x, y) = point(event.clientX.toDouble(), event.clientY.toDouble())
            draw(x, y)
        })
        canvas.addEventListener("mouseup", { drawing = false; panning = false })
        canvas.addEventListener("mouseleave", { drawing = false; panning = false })
        canvas.addEventListener("touchstart", {
            val event = it.unsafeCast<TouchEvent>()
            val touch = event.changedTouches.item(0) ?: return@addEventListener
            if (canvas.getAttribute("data-interaction-mode") == "PAN") {
                panning = true
                panStartX = touch.clientX.toDouble()
                panStartY = touch.clientY.toDouble()
                panStartOffsetX = panOffsetX
                panStartOffsetY = panOffsetY
                it.preventDefault()
                return@addEventListener
            }
            val (x, y) = point(touch.clientX.toDouble(), touch.clientY.toDouble())
            start(x, y)
            it.preventDefault()
        })
        canvas.addEventListener("touchmove", {
            val event = it.unsafeCast<TouchEvent>()
            val touch = event.changedTouches.item(0) ?: return@addEventListener
            if (panning) {
                moveLandscape(touch.clientX.toDouble(), touch.clientY.toDouble())
                it.preventDefault()
                return@addEventListener
            }
            val (x, y) = point(touch.clientX.toDouble(), touch.clientY.toDouble())
            draw(x, y)
            it.preventDefault()
        })
        canvas.addEventListener("touchend", { drawing = false; panning = false; it.preventDefault() })
    }

    LaunchedEffect(initialColor) {
        selectedColor = initialColor
        val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return@LaunchedEffect
        val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return@LaunchedEffect
        ctx.strokeStyle = initialColor
    }

    LaunchedEffect(interactionMode) {
        val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return@LaunchedEffect
        canvas.setAttribute("data-interaction-mode", interactionMode)
        canvas.style.cursor = if (interactionMode == "PAN") "grab" else "crosshair"
    }
}
